import { SerializedPushSubscription } from 'src/api/notifications/notifications';
import { getRewardBloomServiceWorkerRegistration } from 'src/pwa/register-service-worker';

export interface BrowserNotificationRequestResult {
  permission: NotificationPermission;
  subscription: SerializedPushSubscription | null;
}

export type NotificationPromptContext = 'deal' | 'game' | 'pwa-install' | 'saved-opportunity' | 'scheduled-session';
export const NOTIFICATION_PROMPT_CONTEXT_STORAGE_KEY = 'rewardbloom.notification-prompt-context';
export const NOTIFICATION_PROMPT_CONTEXT_EVENT = 'rewardbloom:notification-prompt-context';

export const triggerContextualNotificationPrompt = (context: NotificationPromptContext): void => {
  localStorage.setItem(NOTIFICATION_PROMPT_CONTEXT_STORAGE_KEY, context);
  window.dispatchEvent(
    new CustomEvent<NotificationPromptContext>(NOTIFICATION_PROMPT_CONTEXT_EVENT, { detail: context })
  );
};

export const canUseBrowserNotifications = () =>
  typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;

export const getExistingBrowserNotificationSubscription = async (): Promise<SerializedPushSubscription | null> => {
  if (!canUseBrowserNotifications() || !('PushManager' in window)) {
    return null;
  }

  const registration = await getRewardBloomServiceWorkerRegistration();
  const readyRegistration = await navigator.serviceWorker.ready;
  const subscription = await (registration || readyRegistration).pushManager.getSubscription();

  return subscription ? serializePushSubscription(subscription) : null;
};

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
};

const serializePushSubscription = (subscription: PushSubscription): SerializedPushSubscription | null => {
  const subscriptionJson = subscription.toJSON();

  if (!subscriptionJson.endpoint || !subscriptionJson.keys?.p256dh || !subscriptionJson.keys?.auth) {
    return null;
  }

  return {
    endpoint: subscriptionJson.endpoint,
    expirationTime: subscriptionJson.expirationTime ?? null,
    keys: {
      p256dh: subscriptionJson.keys.p256dh,
      auth: subscriptionJson.keys.auth,
    },
  };
};

export const requestBrowserNotificationSubscription = async (
  vapidPublicKey: string | null
): Promise<BrowserNotificationRequestResult> => {
  if (!canUseBrowserNotifications()) {
    return {
      permission: 'denied',
      subscription: null,
    };
  }

  const permission = await Notification.requestPermission();

  if (permission !== 'granted') {
    return {
      permission,
      subscription: null,
    };
  }

  if (!('PushManager' in window) || !vapidPublicKey) {
    return {
      permission,
      subscription: null,
    };
  }

  const registration = await getRewardBloomServiceWorkerRegistration();
  const readyRegistration = await navigator.serviceWorker.ready;
  const existingSubscription = await readyRegistration.pushManager.getSubscription();

  if (existingSubscription) {
    return {
      permission,
      subscription: serializePushSubscription(existingSubscription),
    };
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });

  return {
    permission,
    subscription: serializePushSubscription(subscription),
  };
};
